/**
 * @file src/components/demo/relationships/RelationshipsGraph.js
 * @description Render the VIS-NETWORK graph for relationships
 */
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {DataSet} from 'vis-data';
import {Network} from 'vis-network';
import Legend from '../Legend';
import { v4 as uuidv4 } from 'uuid'
import urlHandler from '../../../utils/urlHandler';
import RelationshipTooltip from './RelationshipTooltip';
import QuotationMarks from '../../../styles/images/Quotation-Marks-2.png';

/**
 * @class
 * @description Represents a vis-network visualization graph of the document relationships
 * @property relationships - Array of relationship objects
 */
class RelationshipsGraph extends Component {

    /** @constructor */
    constructor(props){
        super(props);

        this.convertToDataSet = this.convertToDataSet.bind(this);
        this.onShowEdgePopup = this.onShowEdgePopup.bind(this);
        this.onShowNodePopup = this.onShowNodePopup.bind(this);
        this.onHidePopup = this.onHidePopup.bind(this);

        this.edgeMapping = {};
        this.nodeMapping = {};
        this.biDirectionalTypes = ['Partner-of', 'Co-Investor-with', 'Competitor-with', 'Relative-of', 'Sibling-Of', 'Spouse/Partner-Of'];

        this.tooltipRef = React.createRef();

        this.externalUrlsDisable = window._externalUrlsDisable;
    };

    /**
     * Utility method to convert the relationships structure to a vis-network dataset.
     * @param {array} relationships - Array of relationship objects
     * @returns vis-network data structure
     */
    convertToDataSet(relationships) {

        let nodes = [];
        let edges = [];
        this.edgeMapping = {};
        this.nodeMapping = {};

        // QUOTES don't have internalRefs, so attempt to add them so we can figure out the graph
        let quotes = {};
        relationships.forEach((rel) => {     
            if ('object' in rel) {
                let obj = rel.object;
                if (obj.type && obj.type == 'QUOTE') {
                    if (obj.anchor && quotes.hasOwnProperty(obj.anchor)) {
                        obj.internalRef = quotes[obj.anchor]
                    } else {
                        obj.internalRef = uuidv4();
                        quotes[obj.anchor] = obj.internalRef;
                    }
                }
            }   
        });

        // Gather all the subject nodes that spawn off of the first document node
        relationships.forEach((rel) => {
            if ('subject' in rel) {
                let anchor = rel.subject.label || rel.subject.anchor;
                let internalRef = rel.subject.internalRef;
                let index = nodes.findIndex((e) => (e.internalRef === internalRef));
                if (index == -1) {
                    index = nodes.length + 1;

                    let subNode = {
                        id: index,
                        label: anchor,
                        shape: 'box',
                        margin: 10,
                        internalRef,
                        font: {
                            size: 24
                        },
                        widthConstraint:{
                            maximum:175
                        }
                    };

                    if ('type' in rel.subject) {
                        let def = Legend.getColorDefinition(rel.subject.type);
                        if (def) {
                            subNode.color = def.background;
                            subNode.font.color = def.color;
                        }
                    }

                    if ('id' in rel.subject && !(index in this.nodeMapping)) {
                        this.nodeMapping[index] = rel.subject;
                    }

                    nodes.push(subNode);
                }
            }           
        });

        // Now add the relationship nodes/edges
        relationships.forEach((rel) => {
            let fromIndex = null;
            let toIndex = null;
            let evidence = null;

            if ('evidence' in rel && 'sentence' in rel.evidence) {
                evidence = rel.evidence.sentence;
            }
            if ('subject' in rel) {
                let internalRef = rel.subject.internalRef  || rel.subject.id;
                fromIndex = nodes.findIndex((e) => (e.internalRef === internalRef)) + 1;
            } 
            if ('object' in rel) {
                let anchor = rel.object.label || rel.object.anchor;
                let internalRef = rel.object.internalRef  || rel.object.id;
                toIndex = nodes.findIndex((e) => (e.internalRef === internalRef));
                if (toIndex == -1) {
                    toIndex = nodes.length + 1;

                    let objNode = {
                        id: toIndex,
                        label: anchor,
                        shape: 'box',
                        margin: 10,
                        internalRef,
                        font: {
                            size: 16
                        }
                    };
    
                    if ('type' in rel.object) {
                        let def = Legend.getColorDefinition(rel.object.type);
                        if (def) {
                            objNode.color = def.background;
                            objNode.font.color = def.color;
                        }

                        if (rel.object.type == 'QUOTE') {
                            if (anchor.length > 50) {
                                objNode.label = `${anchor.substr(0, 50)}...`;
                            }
                            objNode.quote = anchor;
                        }
                    }

                    if (('id' in rel.object || rel.object.type == 'QUOTE') && !(toIndex in this.nodeMapping)) {
                        this.nodeMapping[toIndex] = rel.object;
                    }
    
                    nodes.push(objNode);
                } else {
                    toIndex += 1;
                }
            }

            if ('predicate' in rel && 'type' in rel.predicate && fromIndex != null && toIndex != null) {
                let type = rel.predicate.type;
                if ('properties' in rel.predicate && type in rel.predicate.properties) {
                    type = rel.predicate.properties[type];
                }
                let id = uuidv4();

                let edge = {
                    id,
                    from: fromIndex, 
                    to: toIndex, 
                    evidence,
                    label: type, 
                    width: 2, 
                    color: '#464D52', 
                    arrowStrikethrough:false,
                    arrows: {
                        to: {
                            enabled: true,
                            type: 'arrow',
                            scaleFactor:2,
                        },
                    },
                    font: {
                        align:'middle',
                        size: 24,
                        background: '#ffffff', 
                    },
                };

                if (this.biDirectionalTypes.includes(type)) {
                    edge.arrows.from = {
                        enabled: true,
                        type: 'arrow'
                    }
                }

                edges.push(edge);
                this.edgeMapping[id] = rel;
            }
        });

        return {
            nodes: new DataSet(nodes),
            edges: new DataSet(edges)
        }
    }

    /**
     * Callback after mounting - Render the graph
     */
    componentDidMount() {

        let data = this.convertToDataSet(this.props.relationships);
        var options = {
                physics: {
                    enabled: true,
                    solver: 'repulsion',
                    barnesHut: {
                        theta: 0.55,
                        gravitationalConstant: -6300,
                        springLength: 355,
                        centralGravity: 0,
                        avoidOverlap: 1
                    },
                    repulsion: {
                        nodeDistance: 150,
                        springLength: 500,
                        springConstant: 0.01
                    },
                    stabilization: {
                        enabled: true,
                        fit: false
                    }
                },
                interaction: {
                    hover: true,
                    dragNodes: true,
                    dragView: true,
                    selectable: true
                },
                layout: {
                    randomSeed: '0.2736211630444012:1628808147488',
                }
        }

        var container = document.getElementById('relgraph');
        if (container) {            
            var network = new Network(container, data, options);
            network.on('hoverEdge', (params) => { this.onShowEdgePopup(params) });
            network.on('hoverNode', (params) => { this.onShowNodePopup(params) });
            network.on('blurEdge', (params) => { this.onHidePopup(params) });
            network.on('blurNode', (params) => { this.onHidePopup(params) });

            network.stabilize();
            network.once('stabilizationIterationsDone', () => {
                network.fit();
            })
        }
    }

    /**
     * Callback from vis-network graph when hover over edge is left (blurred)
     */
    onHidePopup() {
        const ref = this.tooltipRef.current;
        if (ref) {
            ref.close();
        }
    }

    /**
     * Callback from vis-network graph when node is hovered over
     * @param {object} params - event parameters
     */
    onShowNodePopup(params) {
        if ('pointer' in params && 'DOM' in params.pointer) {
            const ref = this.tooltipRef.current;
            let obj = this.nodeMapping[params.node];
            if (ref && obj) {
                let info = null;
                    if (obj.type == 'QUOTE') {
                        info = {label: 'Quotation', image: this.externalUrlsDisable ? null: QuotationMarks, description: obj.anchor}
                        ref.open('node', info, [ params.pointer.DOM.x, params.pointer.DOM.y ]);
                    } else {
                        urlHandler.sendRequest('entities2', null, obj.id, null, null, (resp,txt,status) => {
                            if (status === 200) {
                                info = { 
                                    label: resp.label, 
                                    imageUrls : !this.externalUrlsDisable && resp.imageUrls && resp.imageUrls.length>0 ? resp.imageUrls : [], 
                                    description: resp.description 
                                }
                                ref.open('node', info, [ params.pointer.DOM.x, params.pointer.DOM.y ]);
                            }
                        });
                    }
                }
            }
        }

    /**
     * Callback from vis-network graph when edge is hovered over
     * @param {object} params - event parameters
     */
    onShowEdgePopup(params) {
        if ('pointer' in params && 'DOM' in params.pointer) {
            const info = this.edgeMapping[params.edge];
            const ref = this.tooltipRef.current;
            if(ref && info){
                info.bi = this.biDirectionalTypes.includes(info.type)              
                ref.open('edge', info, [ params.pointer.DOM.x, params.pointer.DOM.y ])
            }
        }
    }
    
    render() {
        return (
            <div>
                <RelationshipTooltip ref={this.tooltipRef} placement="topLeft">
                    <div id="relgraph"></div>
                </RelationshipTooltip>
            </div>
        );
    };
}

RelationshipsGraph.propTypes = {
    relationships: PropTypes.array.isRequired
};

export default RelationshipsGraph;